// VgmRecorder — rips an authentic VGM from a running game by tapping its
// sound-chip register writes (docs/sound.md §5). Wrap a capture-capable
// core, run frames (driving it to and through the effect you want —
// ideally a sound-test so no music bleeds in), and call markFrame() once
// per emulated frame; the recorder timestamps each observed write to the
// current frame. toVgm() serializes the result.
//
// The captured (reg, value) numbering matches AudioSynthCapable.write-
// AudioRegister, so the output plays straight back through a synth core.
import { encodeVgm, trimVgmEvents } from "./vgm-encode.js";
export class VgmRecorder {
    core;
    chip;
    events = [];
    frame = 0;
    recording = false;
    constructor(core) {
        this.core = core;
        const chips = core.audioChips();
        const first = chips[0];
        if (first === undefined)
            throw new Error("VgmRecorder: core synthesizes no chip");
        this.chip = first;
    }
    // Begin observing register writes. Frame 0 is the first frame run after
    // this call.
    start() {
        this.events.length = 0;
        this.frame = 0;
        this.recording = true;
        // Prime frame 0 with the channel setup already in effect, so a channel
        // enabled before this call isn't muted on playback (the enabling write
        // happened before the tap was armed; a freshly-reset synth APU boots
        // with every channel disabled). These prime writes precede the first
        // live write at frame 0, so an in-window key-on lands on an enabled
        // channel. See AudioCaptureCapable.audioPrimeState.
        for (const { reg, value } of this.core.audioPrimeState()) {
            this.events.push({ frame: 0, reg, value });
        }
        this.core.onAudioRegisterWrite((reg, value) => {
            if (this.recording)
                this.events.push({ frame: this.frame, reg, value });
        });
    }
    // Advance the recorder's frame clock. Call once per core.frame() (after
    // it returns), so writes made during the NEXT frame are stamped to it.
    markFrame() {
        this.frame++;
    }
    // Stop observing.
    stop() {
        this.recording = false;
        this.core.onAudioRegisterWrite(null);
    }
    // Number of register writes captured so far.
    get writeCount() {
        return this.events.length;
    }
    // Frames elapsed since start() (markFrame count).
    get frameCount() {
        return this.frame;
    }
    // Whether start() has been called and stop() has not.
    get isRecording() {
        return this.recording;
    }
    // Serialize the capture to a .vgm byte blob. `loopFrame` marks where a
    // looping music track should repeat from (omit for one-shot SFX). When
    // `trimTo` (an active frame span from measureActiveFrameSpan) is given,
    // leading silence is collapsed and — if `trimTrailing` — trailing silence
    // dropped, with the loop point shifted to match (docs/sound.md §"Auto-trim").
    toVgm(opts = {}) {
        let events = this.events;
        let totalFrames = this.frame;
        let loopFrame = opts.loopFrame;
        if (opts.trimTo) {
            const trimmed = trimVgmEvents(this.events, this.frame, opts.trimTo, opts.trimTrailing ?? false);
            events = trimmed.events;
            totalFrames = trimmed.totalFrames;
            if (loopFrame !== undefined)
                loopFrame = Math.max(0, loopFrame - opts.trimTo.first);
        }
        return encodeVgm({
            chip: this.chip,
            events,
            totalFrames,
            ...(loopFrame !== undefined ? { loopFrame } : {}),
        });
    }
}
//# sourceMappingURL=vgm-recorder.js.map